import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { forkJoin, Observable, queueScheduler, scheduled } from 'rxjs';
import { CollectionModel } from './models/collection.model';
import { concatAll, map } from 'rxjs/operators';
import { plainToClass } from 'class-transformer';
import { BookmarkModel } from '../bookmarks/models/bookmark.model';
import {
  AddCollectionContentModel,
  CollectionContentModel,
  RemoveCollectionContentModel,
} from './models/collection.content.model';
import {
  OrganizationAttributeModel,
  OrganizationAttributeType,
} from './models/organization-attribute.model';
import { OrganizationUserModel } from './models/organization-user.model';
import { isNil } from 'lodash';
import { normalizeUserRole } from '../auth/models/permissions/permissions';
import { OrganizationUpdateModel } from './models/organization-update';
import {
  OrganizationProfileRequest,
  OrganizationProfileResponse,
} from './models/organization-profile.model';
import { UserRoleModel } from '../auth/models/user-role.model';
import { normalizeBookmarkModel } from '../bookmarks/lib/bookmark-utils';

export interface OrganizationSearchModel {
  group_id: number;
  user_id?: number;
  role_id?: number;
  q?: string;
  page_size?: number;
  offset?: number;
  sort_order?: 'user_email' | 'user_last_name' | 'user_joined_date';
}

export interface OrganizationInviteModel {
  user_email: string;
  role_id?: number;
}

export interface OrganizationInviteResponse {
  email: string;
  reason: string;
  status: 'success' | 'error';
}

export interface OrganizationUserRole {
  roleId: number;
  userId: number;
}

export interface UserContentTransfer {
  sourceUserId: number;
  targetUserId?: number;
}

export interface UserDisplayModel {
  name: string;
  initials: string;
  email: string;
  id: number;
  role: UserRoleModel;
  selected: boolean;
}

export enum CollectionSortOptions {
  IdAsc = '+collection_id',
  IdDesc = '-collection_id',
  NameAsc = '+collection_name',
  NameDesc = '-collection_name',
  UpdateDateAsc = '+collection_updatedate',
  UpdateDateDesc = '-collection_updatedate',
  CreateDateAsc = '+collection_createdate',
  CreateDateDesc = '-collection_createdate',
  CustomAsc = '+custom_sort_order',
  CustomDesc = '-custom_sort_order',
}

export interface GetCollectionsModel {
  group_id: number;
  collection_id?: number;
  content_id?: string;
  content_type?: string;
  include_content?: number;
  sort_order?: CollectionSortOptions[];
}

/**
 * Collections service endpoint
 */
const collectionsURL = '/ws/organization/collection';

// Some services have a limit of 50 users for various operations
const maxUsers = 50;

@Injectable({
  providedIn: 'root',
})
export class OrganizationsService {
  constructor(private http: HttpClient) {}

  createOrganization(organization: OrganizationUpdateModel): Observable<any> {
    return this.http.post('/ws/organization/create', organization);
  }

  updateOrganization(organization: OrganizationUpdateModel): Observable<any> {
    return this.http.post('/ws/organization/update', organization);
  }

  getOrganizationUsers(searchParams: OrganizationSearchModel): Observable<OrganizationUserModel[]> {
    const qs = this.buildSearchParams(searchParams);

    return this.http.get(`/ws/organization/users${qs}`).pipe(
      map((response: any) => response.users),
      // note: normalize user roles
      map((users: OrganizationUserModel[]) => {
        return users.map((user) => {
          return {
            ...user,
            user_role: normalizeUserRole(user.user_role),
          };
        });
      }),
    );
  }

  sendOrganizationInvites(
    group_eid: string,
    organizationInvites: OrganizationInviteModel[],
  ): Observable<OrganizationInviteResponse[]> {
    return this.http
      .post('/ws/organization/invite', {
        version: 2,
        group_eid: group_eid,
        invite_emails: organizationInvites,
      })
      .pipe(
        map((response: any) => {
          const { messages } = response.results_summary;
          return messages;
        }),
      );
  }

  getCollections(params: GetCollectionsModel): Observable<CollectionModel[]> {
    if (!params.sort_order) {
      params.sort_order = [CollectionSortOptions.CustomAsc, CollectionSortOptions.NameDesc];
    }

    const searchParams = this.buildSearchParams(params);

    return this.http.get(`${collectionsURL}${searchParams}`).pipe(
      map((results: []) => {
        return plainToClass(CollectionModel, results).map((collection) =>
          this.normalizeCollectionModel(collection),
        );
      }),
    );
  }

  /**
   * Gets the list of models in the specified collection.
   */
  getCollectionContent(
    groupId: number,
    collectionId: number,
    query = '',
  ): Observable<BookmarkModel[]> {
    const queryParts = query.toLocaleLowerCase().split(' ');

    return this.getCollections({
      group_id: groupId,
      collection_id: collectionId,
      include_content: 1,
    }).pipe(
      map((collections: CollectionModel[]) => {
        const collection = collections[0];

        // exclude unavailable
        return collection.collection_content
          .map((bookmark) => plainToClass(BookmarkModel, bookmark))
          .filter((bookmark: BookmarkModel) => {
            let include = true;

            if (queryParts.length && bookmark.title) {
              queryParts.forEach((queryPart) => {
                include = include && bookmark.title.toLocaleLowerCase().indexOf(queryPart) >= 0;
              });
            }

            include = include && bookmark.unavailable !== true;
            return include;
          });
      }),
    );
  }

  addCollection(groupId: number, collectionName: string) {
    // Note: service currently does not return any data on success.
    return this.http.post(collectionsURL, {
      group_id: groupId,
      collection_name: collectionName,
    });
  }

  removeCollection(collection: CollectionModel) {
    return this.http.request('DELETE', collectionsURL, {
      body: {
        group_id: collection.group_id,
        collection_id: collection.collection_id,
      },
    });
  }

  renameCollection(collection: CollectionModel, collectionName: string) {
    return this.http.post(collectionsURL, {
      group_id: collection.group_id,
      collection_id: collection.collection_id,
      collection_name: collectionName,
    });
  }

  reorderCollections(groupId: number, collections: CollectionModel[]) {
    return this.http.post(`${collectionsURL}/sort-order`, {
      group_id: groupId,
      collection_id_sort_list: collections.map((collection) => collection.collection_id),
    });
  }

  updateCollectionContent(
    groupId: number,
    addContent: AddCollectionContentModel[],
    removeContent: RemoveCollectionContentModel[],
  ) {
    return this.http.post(`${collectionsURL}/content`, {
      group_id: groupId,
      add_content: addContent,
      remove_content: removeContent,
    });
  }

  getOrganizationAttribute(
    attributes: OrganizationAttributeModel[],
    type: OrganizationAttributeType,
    name: string,
  ): OrganizationAttributeModel {
    return attributes.find((attribute) => attribute.type === type && attribute.name === name);
  }

  getPublicProfile(request: OrganizationProfileRequest): Observable<OrganizationProfileResponse> {
    return this.http
      .get(`/ws/organization/public/profile`, { params: { ...request } })
      .pipe(map((results) => results as OrganizationProfileResponse));
  }

  /**
   * Adds the current user to the specified organization (by organization's eid)
   */
  addUserToOrganization(group_eid: string) {
    return this.http.post(`/ws/organization/user/add`, { group_eid: group_eid });
  }

  addUserWithGroupCode(group_code: string): Observable<string> {
    return this.http
      .post(`/ws/organization/user/add`, { group_code: group_code })
      .pipe(map((response: any) => response.organization_name));
  }

  updateUserRoles(groupId: number, users: OrganizationUserRole[]): Observable<boolean> {
    const makeRequest = (usersBatch: OrganizationUserRole[]) => {
      return this.http.post('/ws/organization/users', {
        group_id: groupId,
        update_users: usersBatch.map((user) => {
          return {
            user_id: user.userId,
            role_id: user.roleId,
          };
        }),
      });
    };

    return this.batchUsers(users, makeRequest);
  }

  removeUsers(groupId: number, users: UserContentTransfer[]): Observable<boolean> {
    const makeRequest = (usersBatch: UserContentTransfer[]) => {
      return this.http.post('/ws/organization/users', {
        group_id: groupId,
        remove_users: usersBatch.map((user) => this.createTransferRequest(user)),
      });
    };

    return this.batchUsers(users, makeRequest);
  }

  transferUserContent(groupId: number, users: UserContentTransfer[]): Observable<boolean> {
    const makeRequest = (usersBatch: UserContentTransfer[]) => {
      return this.http.post('/ws/organization/bookmark/transfer', {
        // group id of users
        group_id: groupId,
        // list of transfers
        transfer_bookmarks: usersBatch.map((user) => this.createTransferRequest(user)),
      });
    };

    return this.batchUsers(users, makeRequest);
  }

  // Convert a single request into batches that respect the maximum user limit
  // As recommended by Backend, these requests should be made one at a time to avoid a server timeout.
  private batchUsers(users: OrganizationUserRole[] | UserContentTransfer[], requestFn) {
    let i = 0;
    const requests = [];

    while (i < users.length) {
      const batch = users.slice(i, i + maxUsers);
      requests.push(requestFn(batch));
      i += maxUsers;
    }

    const results = scheduled(requests, queueScheduler).pipe(concatAll());

    return forkJoin(results).pipe(map(() => true));
  }

  createTransferRequest(transfer: UserContentTransfer): any {
    const { sourceUserId, targetUserId } = transfer;

    // add target user: optional
    if (typeof targetUserId === 'number') {
      // optional: transfer to target
      return {
        user_id: sourceUserId,
        // target transfer instructions
        group_bookmarks: {
          // indicate explicit transfer
          transfer_ownership: 1,
          // require: target user (optional on backend, defaults to current user)
          transfer_user_id: targetUserId,
        },
      };
    } else {
      // default
      return {
        user_id: sourceUserId,
      };
    }
  }

  formatUserName(user: OrganizationUserModel): string {
    const { user_first_name, user_last_name } = user;
    const names = [user_first_name, user_last_name].filter((name) => !!name);

    if (names.length > 0) {
      return names.join(' ');
    } else {
      return '';
    }
  }

  formatBadgeLabel(user: OrganizationUserModel): string {
    const { user_first_name, user_last_name } = user;
    const names = [user_first_name, user_last_name].filter((name) => !!name).map((name) => name[0]);

    return names.join('');
  }

  // converts OrganizationUserModel into simplified UserDisplayModel
  createUserDisplayModel(user: OrganizationUserModel): UserDisplayModel {
    return {
      name: this.formatUserName(user),
      initials: this.formatBadgeLabel(user),
      email: user.user_email,
      id: user.user_id,
      role: user.user_role,
      selected: false,
    };
  }

  private normalizeCollectionModel(collection: CollectionModel): CollectionModel {
    if (collection.collection_content) {
      collection.collection_content.forEach((bookmark) => {
        normalizeBookmarkModel(bookmark);
      });
    }
    return collection;
  }

  private buildSearchParams(params: OrganizationSearchModel | GetCollectionsModel): string {
    return (
      '?' +
      Object.entries(params)
        .filter(([_key, value]) => !isNil(value))
        .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
        .join('&')
    );
  }
}
